/**
 * Research Engine — knowledge-fragment validator.
 *
 * A fragment is a partial Research Brief contributed by one knowledge source.
 * Validation keeps the intelligence layer honest: only known fields, correct
 * types, valid genome/pattern references, and a sane confidence/asOf. Runs at
 * load so a bad fragment fails fast rather than corrupting a composed brief.
 * Deterministic and non-throwing.
 */

import { ALLOWED_FRAGMENT_KEYS, PROSE_FIELDS, LIST_FIELDS, MERGE, RESEARCH_FIELDS } from './researchSchema';
import { GENOMES } from '../genome';
import { LIBRARY_PATTERNS } from '../genome/vocabulary';

const GENOME_IDS = new Set(GENOMES.map((g) => g.id));
const PATTERNS = new Set(LIBRARY_PATTERNS);
const MERGE_BY_KEY = new Map(RESEARCH_FIELDS.map((f) => [f.key, f.merge]));

const isStr = (v) => typeof v === 'string' && v.length > 0;
const isArrOfStr = (v) => Array.isArray(v) && v.every(isStr);

/**
 * @param {*} fragment
 * @param {string} [label]  Source label for error messages.
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateFragment = (fragment, label = 'fragment') => {
  const errors = [];
  if (fragment === null || typeof fragment !== 'object' || Array.isArray(fragment)) {
    return { valid: false, errors: [`${label}: must be an object`] };
  }

  Object.keys(fragment).forEach((key) => {
    if (!ALLOWED_FRAGMENT_KEYS.includes(key)) errors.push(`${label}.${key}: unknown field`);
  });

  PROSE_FIELDS.forEach((key) => {
    if (fragment[key] !== undefined && !isStr(fragment[key])) errors.push(`${label}.${key}: must be a non-empty string`);
  });

  LIST_FIELDS.forEach((key) => {
    if (fragment[key] === undefined) return;
    if (!isArrOfStr(fragment[key])) { errors.push(`${label}.${key}: must be an array of non-empty strings`); return; }
    const merge = MERGE_BY_KEY.get(key);
    if (merge === MERGE.GENOME_REF) {
      fragment[key].forEach((id) => { if (!GENOME_IDS.has(id)) errors.push(`${label}.${key}: "${id}" is not a known genome`); });
    } else if (merge === MERGE.PATTERN_REF) {
      fragment[key].forEach((p) => { if (!PATTERNS.has(p)) errors.push(`${label}.${key}: "${p}" is not a known library pattern`); });
    }
  });

  if (fragment.confidence !== undefined && (typeof fragment.confidence !== 'number' || fragment.confidence < 0 || fragment.confidence > 1)) {
    errors.push(`${label}.confidence: must be a number in [0,1]`);
  }
  if (fragment.asOf !== undefined && !isStr(fragment.asOf)) errors.push(`${label}.asOf: must be a non-empty string`);
  if (fragment.note !== undefined && !isStr(fragment.note)) errors.push(`${label}.note: must be a non-empty string`);

  return { valid: errors.length === 0, errors };
};
